import React from "react";

/**
 * "How It Works" — editorial poster layout
 * Side-by-side steps separated by thin hairlines.
 * Numerals become huge background watermarks behind the copy.
 * Final step has a warm orange tint + Done-Right stamp.
 * No grids, no crosshairs, no connectors. Pure typography-led.
 */

const STEPS = [
  {
    num: "01",
    eyebrow: "Submit",
    title: "Drop your project.",
    body:
      "Our proprietary AI turns photos, your vision, and your budget into a clean, contractor-ready brief — no jargon, no back-and-forth.",
  },
  {
    num: "02",
    eyebrow: "Match & Secure",
    title: "Get matched. Funds stay safe.",
    body:
      "We pair you with vetted local pros who've actually built this before. Your payment is held safely with MeinHaus — released only on your approval.",
  },
  {
    num: "03",
    eyebrow: "Build",
    title: "Watch it come together.",
    body:
      "Track every milestone live with daily progress photos and direct chat. If anything's off, our Done-Right Guarantee makes it right at no cost.",
  },
  {
    num: "04",
    eyebrow: "Handoff",
    title: "Final walkthrough. Keys in hand.",
    body:
      "You sign off, payment releases, project closes. We stick around for warranty and follow-ups — peace of mind doesn't end at the last brushstroke.",
    final: true,
  },
];

const StepPanel = ({ step, isLast }) => {
  const { num, eyebrow, title, body, final } = step;

  return (
    <article
      className="relative px-5 sm:px-6 lg:px-8 py-8 lg:py-14 overflow-hidden h-full flex flex-col"
      style={{
        backgroundColor: final ? "rgba(242,107,31,0.05)" : "transparent",
      }}
    >
      {/* Watermark numeral — sits behind the copy, top-left corner */}
      <span
        aria-hidden="true"
        className="absolute pointer-events-none select-none font-black leading-none"
        style={{
          left: "-6px",
          top: "-12px",
          fontSize: "clamp(110px, 10vw, 160px)",
          letterSpacing: "-0.06em",
          color: final ? "rgba(242,107,31,0.12)" : "rgba(15,23,42,0.045)",
          fontFamily: "'Inter', system-ui, sans-serif",
        }}
      >
        {num}
      </span>

      {/* Top accent rule + step label */}
      <div className="relative flex items-center gap-3 mb-10 min-h-[20px]">
        <span
          className="block h-[2px] w-9 flex-shrink-0"
          style={{ backgroundColor: "#1AA0DD" }}
        />
        <span
          className="text-[11px] font-bold tracking-[0.24em] uppercase whitespace-nowrap"
          style={{ color: "#1AA0DD" }}
        >
          {eyebrow}
        </span>
      </div>

      {/* Title */}
      <h3
        className="relative font-extrabold text-[#0f172a] tracking-tight text-[22px] sm:text-[28px] lg:text-[28px] xl:text-[32px] leading-[1.12] lg:min-h-[108px] xl:min-h-[124px]"
        style={{ fontFamily: "'Inter', system-ui, sans-serif" }}
      >
        {title}
      </h3>

      {/* Body */}
      <p className="relative mt-5 text-[#475569] text-[14.5px] leading-[1.65] max-w-[340px]">
        {body}
      </p>

      {/* Get A Quote CTA on final step */}
      {final && (
        <div className="relative mt-auto pt-7 flex items-center h-[74px]">
          <a
            href="#quote"
            data-testid="how-it-works-cta-quote"
            className="group inline-flex items-center gap-2 px-6 py-3 rounded-full font-bold text-white text-[14px] tracking-tight transition-all duration-300 hover:-translate-y-0.5"
            style={{
              backgroundColor: "#F26B1F",
              border: "2px solid #FFFFFF",
              boxShadow: "0 10px 24px -10px rgba(242,107,31,0.55), 0 0 0 1px rgba(242,107,31,0.18)",
            }}
          >
            Get A Quote
            <svg
              className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1"
              viewBox="0 0 16 16"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M3 8 H13 M9 4 L13 8 L9 12"
                stroke="currentColor"
                strokeWidth="2.4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </a>
        </div>
      )}

      {/* Grey "next-step" arrow at the bottom-right of non-final panels */}
      {!isLast && (
        <div
          aria-hidden="true"
          className="relative mt-auto pt-7 flex justify-end items-center h-[74px]"
        >
          <svg
            width="36"
            height="14"
            viewBox="0 0 36 14"
            fill="none"
            className="text-slate-500"
          >
            <path
              d="M1 7 H32 M27 2 L33 7 L27 12"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      )}
    </article>
  );
};

const HowItWorksSection = () => {
  return (
    <section
      data-testid="how-it-works-section"
      className="relative w-full bg-white pt-16 sm:pt-24 lg:pt-32 pb-16 sm:pb-24 lg:pb-32"
    >
      <div className="relative max-w-[1280px] mx-auto px-5 sm:px-8">
        {/* ---------- Header ---------- */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-12 items-end mb-16 lg:mb-20">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 mb-5">
              <span className="block h-px w-8 bg-[#1AA0DD]" />
              <span className="text-[11px] sm:text-[12px] font-bold tracking-[0.24em] uppercase text-[#1AA0DD]">
                How it works
              </span>
            </div>
            <h2
              className="font-extrabold text-[#0f172a] tracking-tight text-[26px] sm:text-[32px] lg:text-[40px] leading-[1.06]"
              style={{ fontFamily: "'Inter', system-ui, sans-serif" }}
            >
              From idea to{" "}
              <span style={{ color: "#F26B1F" }}>keys in hand</span>.
              <br className="hidden sm:block" />
              Four steps. Zero guesswork.
            </h2>
          </div>
          <div className="lg:col-span-5 lg:pb-2">
            <p className="text-[#475569] text-[15px] sm:text-[16px] leading-[1.7] max-w-[440px]">
              The renovation process homeowners <em>wished</em> existed.
              Transparent quotes, vetted pros, milestone-by-milestone updates,
              and payment that releases only when{" "}
              <strong className="text-[#0f172a]">you</strong> sign off.
            </p>
          </div>
        </div>

        {/* ---------- Steps row ---------- */}
        <div className="relative">
          {/* Top hairline */}
          <span
            aria-hidden="true"
            className="absolute top-0 left-0 right-0 h-px bg-slate-200"
          />
          {/* Bottom hairline */}
          <span
            aria-hidden="true"
            className="absolute bottom-0 left-0 right-0 h-px bg-slate-200"
          />

          <ol
            className="
              relative grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4
              divide-y sm:divide-y-0 sm:divide-x divide-slate-200
              [&>li:nth-child(2)]:sm:divide-y-0
              [&>li:nth-child(3)]:sm:border-t [&>li:nth-child(3)]:sm:border-slate-200
              [&>li:nth-child(4)]:sm:border-t [&>li:nth-child(4)]:sm:border-slate-200
              lg:[&>li:nth-child(3)]:border-t-0 lg:[&>li:nth-child(4)]:border-t-0
            "
          >
            {STEPS.map((step, i) => (
              <li key={step.num} className="relative">
                <StepPanel step={step} isLast={i === STEPS.length - 1} />
              </li>
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
};

export default HowItWorksSection;
